import { useEffect, useState } from 'react';
import { Button } from '../../components/ui/Button';
import { Input, Select } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { Icon } from '../../components/ui/Icon';
import { DIAS_SEMANA } from '../../../data/repositories/RepositorioUmbrales';
import type {
  DatosReglaHoraria,
  ReglaHoraria,
} from '../../../data/repositories/RepositorioAutomatizacion';
import type { Actuador } from '../../../domain/entities/Actuador';
import estilos from '../umbrales/Umbrales.module.css';

interface FormularioReglaHorariaProps {
  abierto: boolean;
  regla: ReglaHoraria | null;
  actuadores: readonly Actuador[];
  guardando: boolean;
  onCerrar: () => void;
  onGuardar: (datos: DatosReglaHoraria) => void;
}

interface Campos {
  actuadorId: string;
  horaInicio: string;
  horaFin: string;
  dias: number[];
  accion: string;
  pulsoMs: string;
  activo: boolean;
}

const CAMPOS_INICIALES: Campos = {
  actuadorId: '',
  horaInicio: '08:00',
  horaFin: '18:00',
  dias: [1, 2, 3, 4, 5],
  accion: 'encender',
  pulsoMs: '500',
  activo: true,
};

const ACCIONES = [
  { valor: 'encender', etiqueta: 'Encenderlo' },
  { valor: 'apagar', etiqueta: 'Apagarlo' },
  { valor: 'pulso', etiqueta: 'Darle un pulso' },
];

/** Deduce qué acción representa la orden guardada. */
function accionDe(regla: ReglaHoraria): string {
  if (regla.tipoOrden === 'pulse') return 'pulso';
  return String(regla.carga.v ?? '').toUpperCase() === 'OFF' ? 'apagar' : 'encender';
}

/** Alta y edición de una regla que acciona un equipo por reloj. */
export function FormularioReglaHoraria({
  abierto,
  regla,
  actuadores,
  guardando,
  onCerrar,
  onGuardar,
}: FormularioReglaHorariaProps) {
  const [campos, setCampos] = useState<Campos>(CAMPOS_INICIALES);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!abierto) return;

    setError(null);
    setCampos(
      regla
        ? {
            actuadorId: String(regla.actuadorId),
            horaInicio: regla.horaInicio || '08:00',
            horaFin: regla.horaFin || '18:00',
            dias: [...regla.dias],
            accion: accionDe(regla),
            pulsoMs: String(regla.carga.pulse_ms ?? 500),
            activo: regla.activo,
          }
        : CAMPOS_INICIALES,
    );
  }, [abierto, regla]);

  const alternarDia = (dia: number) => {
    setCampos((c) => ({
      ...c,
      dias: c.dias.includes(dia)
        ? c.dias.filter((d) => d !== dia)
        : [...c.dias, dia].sort((a, b) => a - b),
    }));
  };

  const enviar = () => {
    setError(null);

    const actuadorId = Number(campos.actuadorId);
    if (!Number.isFinite(actuadorId) || actuadorId <= 0) {
      setError('Elige el equipo que se va a accionar.');
      return;
    }
    if (campos.dias.length === 0) {
      setError('Marca al menos un día de la semana.');
      return;
    }
    if (campos.horaInicio === campos.horaFin) {
      setError('La hora de inicio y la de fin no pueden ser la misma.');
      return;
    }

    const esPulso = campos.accion === 'pulso';
    const pulsoMs = Number(campos.pulsoMs);

    if (esPulso && (!Number.isFinite(pulsoMs) || pulsoMs < 50 || pulsoMs > 30000)) {
      setError('La duración del pulso debe estar entre 50 y 30000 milisegundos.');
      return;
    }

    onGuardar({
      actuador_id: actuadorId,
      hora_inicio: campos.horaInicio,
      hora_fin: campos.horaFin,
      dias: campos.dias,
      cmd_kind: esPulso ? 'pulse' : 'set',
      cmd_payload: esPulso
        ? { pulse_ms: pulsoMs }
        : { v: campos.accion === 'apagar' ? 'OFF' : 'ON' },
      activo: campos.activo,
    });
  };

  return (
    <Modal
      abierto={abierto}
      onCerrar={onCerrar}
      titulo={regla ? 'Editar regla horaria' : 'Nueva regla horaria'}
      descripcion="El equipo se accionará solo dentro de esta franja, sin depender de ningún sensor."
      pie={
        <>
          <Button variante="secundario" onClick={onCerrar}>
            Cancelar
          </Button>
          <Button onClick={enviar} cargando={guardando} icono="guardar">
            Guardar
          </Button>
        </>
      }
    >
      <div className={estilos.formulario}>
        {error && <div className={estilos.errorFormulario}>{error}</div>}

        <div className={estilos.filaCampos}>
          <Select
            etiqueta="Equipo"
            requerido
            placeholder="Elige un equipo"
            value={campos.actuadorId}
            onChange={(e) => setCampos((c) => ({ ...c, actuadorId: e.target.value }))}
            opciones={actuadores.map((a) => ({
              valor: a.id,
              etiqueta: a.nodoNombre ? `${a.nombre} · ${a.nodoNombre}` : a.nombre,
            }))}
          />
          <Select
            etiqueta="Qué hacer"
            value={campos.accion}
            onChange={(e) => setCampos((c) => ({ ...c, accion: e.target.value }))}
            opciones={ACCIONES}
          />
        </div>

        {campos.accion === 'pulso' && (
          <Input
            etiqueta="Duración del pulso (ms)"
            type="number"
            min={50}
            max={30000}
            step={50}
            value={campos.pulsoMs}
            onChange={(e) => setCampos((c) => ({ ...c, pulsoMs: e.target.value }))}
          />
        )}

        <div className={estilos.ventana}>
          <div className={estilos.ventanaHoras}>
            <label className={estilos.campoHora}>
              <span>Desde</span>
              <input
                type="time"
                value={campos.horaInicio}
                onChange={(e) => setCampos((c) => ({ ...c, horaInicio: e.target.value }))}
              />
            </label>
            <label className={estilos.campoHora}>
              <span>Hasta</span>
              <input
                type="time"
                value={campos.horaFin}
                onChange={(e) => setCampos((c) => ({ ...c, horaFin: e.target.value }))}
              />
            </label>
          </div>

          <div className={estilos.dias}>
            {DIAS_SEMANA.map((nombre, dia) => {
              const activo = campos.dias.includes(dia);
              return (
                <button
                  key={dia}
                  type="button"
                  className={[estilos.dia, activo ? estilos.diaActivo : '']
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => alternarDia(dia)}
                  aria-pressed={activo}
                  title={nombre}
                >
                  {nombre.slice(0, 1)}
                </button>
              );
            })}
          </div>

          {campos.dias.length === 0 && (
            <span className={estilos.avisoMenor}>
              <Icon nombre="advertenciaContorno" tamano={13} />
              Sin días marcados la regla no se ejecutará nunca.
            </span>
          )}
        </div>

        <div className={estilos.opciones}>
          <label className={estilos.opcion}>
            <input
              type="checkbox"
              checked={campos.activo}
              onChange={(e) => setCampos((c) => ({ ...c, activo: e.target.checked }))}
            />
            <span>
              <strong>Regla activa</strong>
              <span className={estilos.opcionAyuda}>Si se desactiva, deja de ejecutarse</span>
            </span>
          </label>
        </div>
      </div>
    </Modal>
  );
}
